/**
 * The card's On/Off switch (POS2-1137 CP4).
 *
 * Pause and resume go through the same `PUT /reports/{type}` a save does, carrying the schedule's
 * current settings with `is_paused` flipped. Not a dedicated endpoint: the backend already derives
 * `paused` / `resumed` / `updated` from the before and after rows, so a toggle is a save that
 * changes exactly one field and lands the right audit action for free.
 *
 * **A never-configured report has no switch to flip and this hook is never asked to.** Its toggle
 * opens the editor instead — a report can never go live unseen — which is a decision the card
 * makes before it gets here.
 *
 * `expected_updated_at` is deliberately absent. A toggle is one field and there is nothing to lose
 * to somebody else's edit, so telling the person about a concurrent change would be noise on an
 * action that cannot have been affected by it.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { useTranslation } from "@/i18n/use-translation";
import { apiError } from "@/lib/api-error";
import type { ReportSchedule } from "@/hooks/use-reports";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

export function useToggleSchedule(siteId: string | null) {
  const { t } = useTranslation();
  const headers = useApiHeaders();
  const queryClient = useQueryClient();

  return useMutation<ReportSchedule, Error, { schedule: ReportSchedule; paused: boolean }>({
    mutationFn: async ({ schedule, paused }) => {
      const res = await fetch(
        `${API_URL}/api/v1/reports/${schedule.report_type}?site_id=${siteId}`,
        {
          method: "PUT",
          headers,
          body: JSON.stringify({
            cadence: schedule.cadence,
            send_dow: schedule.send_dow,
            send_time_local: schedule.send_time_local,
            enabled_sections: schedule.enabled_sections,
            is_paused: paused,
          }),
        },
      );
      if (!res.ok) throw await apiError(t, res);
      return ((await res.json()) as { data: ReportSchedule }).data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["reports"] }),
  });
}
